// Product form component for creating and editing products
import React, { useMemo, useState } from 'react';
import { Product, Stock } from '../types';
import { CATEGORIES } from '../shared/categories';
import { ProductOptionsManager } from './ProductOptionsManager';
import ProductStockSelector from './ProductStockSelector';
import { AllergenManager } from './AllergenManager';
import './ProductForm.css';

interface ProductFormProps {
    initialData?: Product | null;
    stocks: Stock[];
    stocksLoading?: boolean;
    onSubmit: (product: Omit<Product, 'id' | 'availableStock' | 'stockBreakdown'>) => Promise<void> | void;
    onUpdateStocks?: (updates: Array<{ stockId: string; quantity: number }>) => Promise<void> | void;
    onRefreshStocks?: () => Promise<void> | void;
    onCancel: () => void;
    isSubmitting: boolean;
}

const ProductForm: React.FC<ProductFormProps> = ({
    initialData,
    stocks,
    stocksLoading = false,
    onSubmit,
    onUpdateStocks,
    onRefreshStocks,
    onCancel,
    isSubmitting
}) => {
    const [formData, setFormData] = useState({
        name: initialData?.name || '',
        description: initialData?.description || '',
        detailedDescription: initialData?.detailedDescription || '',
        price: initialData?.price || 0,
        originalPrice: initialData?.originalPrice || undefined,
        campaignText: initialData?.campaignText || '',
        category: initialData?.category || 'pizza',
        image: initialData?.image || '',
        available: initialData?.available ?? true,
        allowStockException: initialData?.allowStockException ?? false,
        options: initialData?.options || [],
        allergens: initialData?.allergens || [],
        sortOrder: initialData?.sortOrder ?? 0,
    });

    const [selectedStockIds, setSelectedStockIds] = useState<string[]>(initialData?.stockIds || []);
    const [stockQuantityOverrides, setStockQuantityOverrides] = useState<Record<string, number>>(() => {
        const overrides: Record<string, number> = {};
        (initialData?.stockBreakdown || []).forEach((entry) => {
            overrides[entry.stockId] = entry.quantity;
        });
        return overrides;
    });

    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    const stockMap = useMemo(() => new Map(stocks.map((stock): [string, Stock] => [stock.id, stock])), [stocks]);

    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};

        if (!formData.name.trim()) {
            errors.name = 'Product name is required';
        }

        if (!formData.description.trim()) {
            errors.description = 'Product description is required';
        } if (formData.price <= 0) {
            errors.price = 'Price must be greater than 0';
        }

        if (!formData.image.trim()) {
            errors.image = 'Image URL is required';
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        const productPayload = {
            name: formData.name.trim(),
            description: formData.description.trim(),
            detailedDescription: formData.detailedDescription.trim() || undefined,
            price: Number(formData.price),
            originalPrice: formData.originalPrice && formData.originalPrice > 0 ? Number(formData.originalPrice) : undefined,
            campaignText: formData.campaignText.trim() || undefined,
            category: formData.category,
            image: formData.image.trim(),
            available: formData.available,
            allowStockException: formData.allowStockException,
            stockIds: selectedStockIds,
            options: formData.options,
            allergens: formData.allergens,
            sortOrder: formData.sortOrder,
        } as Omit<Product, 'id' | 'availableStock' | 'stockBreakdown'>;

        const stockUpdates: Array<{ stockId: string; quantity: number }> = [];

        selectedStockIds.forEach((stockId) => {
            const override = stockQuantityOverrides[stockId];
            const currentQuantity = stockMap.get(stockId)?.quantity;
            if (override !== undefined && currentQuantity !== undefined && override !== currentQuantity) {
                stockUpdates.push({ stockId, quantity: override });
            }
        });

        try {
            if (stockUpdates.length > 0 && onUpdateStocks) {
                await onUpdateStocks(stockUpdates);
            }

            await onSubmit(productPayload);
        } catch (error) {
            console.error('Failed to submit product form:', error);
        }
    };

    const handleInputChange = (field: string, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));

        // Clear field error when user starts typing
        if (fieldErrors[field]) {
            setFieldErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const handleToggleStock = (stockId: string) => {
        setSelectedStockIds(prev => {
            if (prev.includes(stockId)) {
                const next = prev.filter(id => id !== stockId);
                setStockQuantityOverrides(current => {
                    const { [stockId]: _removed, ...rest } = current;
                    return rest;
                });
                return next;
            }

            const next = [...prev, stockId];
            if (stockQuantityOverrides[stockId] === undefined) {
                const defaultQuantity = stockMap.get(stockId)?.quantity ?? 0;
                setStockQuantityOverrides(current => ({ ...current, [stockId]: defaultQuantity }));
            }
            return next;
        });
    };

    const handleStockQuantityChange = (stockId: string, quantity: number) => {
        setStockQuantityOverrides(prev => ({ ...prev, [stockId]: Math.max(0, Math.floor(quantity)) }));
    };

    return (
        <div className="product-form">
            <form onSubmit={handleSubmit}>
                {/* Sticky top action bar */}
                <div className="form-actions-top">
                    <button
                        type="submit"
                        className="submit-btn"
                        disabled={isSubmitting}
                    >
                        {isSubmitting
                            ? (initialData ? 'Updating...' : 'Creating...')
                            : (initialData ? 'Update Product' : 'Create Product')
                        }
                    </button>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="name">Product Name *</label>
                        <input
                            type="text"
                            id="name"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            className={fieldErrors.name ? 'error' : ''}
                            disabled={isSubmitting}
                            placeholder="e.g., Margherita Pizza"
                        />
                        {fieldErrors.name && (
                            <span className="field-error">{fieldErrors.name}</span>
                        )}
                    </div>

                    <div className="form-group">
                        <label htmlFor="category">Category *</label>
                        <select
                            id="category"
                            value={formData.category}
                            onChange={(e) => handleInputChange('category', e.target.value)}
                            disabled={isSubmitting}
                        >
                            {CATEGORIES.map((cat) => (
                                <option key={cat.value} value={cat.value}>
                                    {cat.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="form-group">
                    <label htmlFor="description">Description *</label>
                    <textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        className={fieldErrors.description ? 'error' : ''}
                        disabled={isSubmitting}
                        rows={3}
                        placeholder="Describe the product..."
                    />
                    {fieldErrors.description && (
                        <span className="field-error">{fieldErrors.description}</span>
                    )}
                    <div className="field-help">
                        <small>Short description shown in product cards</small>
                    </div>
                </div>

                <div className="form-group">
                    <label htmlFor="detailedDescription">Detailed Description</label>
                    <textarea
                        id="detailedDescription"
                        value={formData.detailedDescription}
                        onChange={(e) => handleInputChange('detailedDescription', e.target.value)}
                        disabled={isSubmitting}
                        rows={5}
                        placeholder="Add more detailed information about ingredients, preparation, or serving suggestions (optional)..."
                    />
                    <div className="field-help">
                        <small>Optional. Shown in product details modal when customers customize their order</small>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="price">Price (£) *</label>
                        <input
                            type="number"
                            id="price"
                            value={formData.price}
                            onChange={(e) => handleInputChange('price', parseFloat(e.target.value) || 0)}
                            className={fieldErrors.price ? 'error' : ''}
                            disabled={isSubmitting}
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                        />
                        {fieldErrors.price && (
                            <span className="field-error">{fieldErrors.price}</span>
                        )}
                        <div className="field-help">
                            <small>Actual selling price (discounts applied via promo codes)</small>
                        </div>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="campaignText">Campaign Text</label>
                        <input
                            type="text"
                            id="campaignText"
                            value={formData.campaignText}
                            onChange={(e) => handleInputChange('campaignText', e.target.value)}
                            disabled={isSubmitting}
                            placeholder='e.g., "Limited time offer!" or "20% off today!"'
                        />
                        <div className="field-help">
                            <small>Optional. Promotional message displayed as a badge</small>
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="sortOrder">Sort Order</label>
                        <input
                            type="number"
                            id="sortOrder"
                            value={formData.sortOrder}
                            onChange={(e) => handleInputChange('sortOrder', parseInt(e.target.value) || 0)}
                            disabled={isSubmitting}
                            min="0"
                            step="1"
                            placeholder="0"
                        />
                        <div className="field-help">
                            <small>Display order within category (lower numbers appear first)</small>
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <ProductStockSelector
                        stocks={stocks}
                        selectedStockIds={selectedStockIds}
                        stockQuantityOverrides={stockQuantityOverrides}
                        onToggleStock={handleToggleStock}
                        onOverrideChange={handleStockQuantityChange}
                        loading={stocksLoading}
                        disabled={isSubmitting}
                        onStocksChanged={async () => {
                            if (onRefreshStocks) {
                                await onRefreshStocks();
                            }
                        }}
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="available">Status</label>
                    <div className="checkbox-group">
                        <input
                            type="checkbox"
                            id="available"
                            checked={formData.available}
                            onChange={(e) => handleInputChange('available', e.target.checked)}
                            disabled={isSubmitting}
                        />
                        <label htmlFor="available" className="checkbox-label">
                            Available for ordering
                        </label>
                    </div>
                    <div className="checkbox-group" style={{ marginTop: '0.5rem' }}>
                        <input
                            type="checkbox"
                            id="allowStockException"
                            checked={formData.allowStockException}
                            onChange={(e) => handleInputChange('allowStockException', e.target.checked)}
                            disabled={isSubmitting}
                        />
                        <label htmlFor="allowStockException" className="checkbox-label">
                            Allow Stock Exception (Can order even if out of stock)
                        </label>
                    </div>
                </div>

                <div className="form-group">
                    <label htmlFor="image">Image URL *</label>
                    <input
                        type="text"
                        id="image"
                        value={formData.image}
                        onChange={(e) => handleInputChange('image', e.target.value)}
                        className={fieldErrors.image ? 'error' : ''}
                        disabled={isSubmitting}
                        placeholder="/images/product.jpg or https://example.com/image.jpg"
                    />
                    {fieldErrors.image && (
                        <span className="field-error">{fieldErrors.image}</span>
                    )}
                    <div className="image-help">
                        <small>Use relative paths like "/images/pizza.jpg" or full URLs</small>
                    </div>
                </div>

                {formData.image && (
                    <div className="image-preview">
                        <label>Preview:</label>
                        <img
                            src={formData.image}
                            alt="Product preview"
                            onLoad={(e) => {
                                e.currentTarget.style.display = 'block';
                            }}
                        />
                    </div>
                )}

                {/* Product Options Management */}
                <div className="form-section">
                    <h3>Product Options</h3>
                    <p className="section-description">
                        Configure customization options for this product (e.g., sizes, toppings, add-ons).
                    </p>
                    <ProductOptionsManager
                        options={formData.options}
                        onChange={(options) => handleInputChange('options', options)}
                        stocks={stocks}
                        stocksLoading={stocksLoading}
                        onRefreshStocks={onRefreshStocks}
                        disabled={isSubmitting}
                    />
                </div>

                {/* Allergen Information */}
                <div className="form-section">
                    <div className="selector-toolbar">
                        <span className="section-label">Allergens</span>
                        <AllergenManager
                            allergens={formData.allergens}
                            onChange={(allergens) => handleInputChange('allergens', allergens)}
                            disabled={isSubmitting}
                        />
                    </div>
                    <p className="section-description">
                        Add allergen information specific to this product's recipe or preparation method.
                        Allergens from linked stock items and options will be automatically included.
                    </p>
                </div>

                <div className="form-actions">
                    <button
                        type="button"
                        className="cancel-btn"
                        onClick={onCancel}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="submit-btn"
                        disabled={isSubmitting}
                    >
                        {isSubmitting
                            ? (initialData ? 'Updating...' : 'Creating...')
                            : (initialData ? 'Update Product' : 'Create Product')
                        }
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ProductForm;
